// CRM-NANCOZI — Centre de notifications unifié
import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchNotifications, envoyerNotification } from '../store/slices/notificationsSlice';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  AlertTriangle, CalendarClock, CreditCard, Gift,
  Bell, Send, RefreshCw, MessageSquarePlus, X,
  Search, FileText, Eye, History,
} from 'lucide-react';

/* ── Helpers ── */
const fmt   = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const diffJ = d => Math.ceil((new Date(d) - new Date()) / 86400000);
const fmtN  = n => Number(n || 0).toLocaleString('fr-FR');

const tog = setter => v =>
  setter(s => { const n = new Set(s); n.has(v) ? n.delete(v) : n.add(v); return n; });

const urgenceOf = j => j <= 15 ? 'echu' : j <= 30 ? 'j30' : 'j60';

function UrgenceBadge({ jours }) {
  const [color, bg, label] =
    jours < 0   ? ['#C00000', '#FEE2E2', `Échu (${Math.abs(jours)}j)`] :
    jours <= 15 ? ['#C00000', '#FEE2E2', `J-${jours}`] :
    jours <= 30 ? ['#ED7D31', '#FEF3C7', `J-${jours}`] :
                  ['#2E75B6', '#EBF3FB', `J-${jours}`];
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px',
      borderRadius: 99, color, background: bg }}>{label}</span>
  );
}

function SendBtn({ onClick, sending }) {
  return (
    <button className="header-btn btn-primary btn-sm" onClick={onClick} disabled={sending}
      style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
      <Send size={11} /> {sending ? '…' : 'Notifier'}
    </button>
  );
}

function FilterBar({ options, active, setActive, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 600 }}>{label} :</span>
      {options.map(({ value, label: lbl, color }) => (
        <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 5,
          cursor: 'pointer', fontSize: 12, userSelect: 'none',
          fontWeight: active.has(value) ? 700 : 400,
          color: active.has(value) ? color : '#6B7280' }}>
          <input type="checkbox" checked={active.has(value)}
            onChange={() => tog(setActive)(value)}
            style={{ accentColor: color, cursor: 'pointer', width: 14, height: 14 }} />
          {lbl}
        </label>
      ))}
    </div>
  );
}

const PERSO_INIT = { client: '', canal: 'SMS', type_not: 'message_special', contenu: '', date_evenement: '' };

const URGENCE_OPT = [
  { value: 'echu', label: 'Échu / J-15', color: '#C00000' },
  { value: 'j30',  label: 'J-16 à J-30', color: '#ED7D31' },
  { value: 'j60',  label: 'J-31 à J-60', color: '#2E75B6' },
];
const PAIEMENT_OPT = [
  { value: 'impaye',  label: 'Impayé',  color: '#C00000' },
  { value: 'partiel', label: 'Partiel', color: '#ED7D31' },
];
const HIST_TYPE_OPT = [
  { value: 'message_special', label: 'Message spécial',      color: '#1F4E79' },
  { value: 'contractuelle',   label: 'Alerte contractuelle',  color: '#ED7D31' },
  { value: 'impaye',          label: 'Alerte impayé',         color: '#C00000' },
  { value: 'anniversaire',    label: 'Anniversaire',          color: '#1E7145' },
  { value: 'fete',            label: 'Fête',                  color: '#6A0DAD' },
];
const CANAL_OPT = [
  { value: 'SMS',      label: 'SMS',      color: '#ED7D31' },
  { value: 'Email',    label: 'Email',    color: '#1F4E79' },
  { value: 'WhatsApp', label: 'WhatsApp', color: '#1E7145' },
];
const STAT_OPT = [
  { value: 'envoye',     label: 'Envoyé',     color: '#1E7145' },
  { value: 'en_attente', label: 'En attente', color: '#ED7D31' },
  { value: 'echec',      label: 'Échec',      color: '#C00000' },
];

const TABS = [
  { key: 'critiques',    label: 'Alertes critiques', color: '#C00000', icon: AlertTriangle },
  { key: 'rappels',      label: 'Rappels',            color: '#ED7D31', icon: Bell },
  { key: 'historique',   label: 'Historique',         color: '#1F4E79', icon: History },
];

export default function NotificationsPage() {
  const dispatch = useDispatch();
  const { liste: sentList, loading: loadingNotifs } = useSelector(s => s.notifications);

  const [tab,        setTab]        = useState('critiques');
  const [coachings,  setCoachings]  = useState([]);
  const [clients,    setClients]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [sending,    setSending]    = useState(null);
  const [pdfLoading, setPdfLoading] = useState(null);
  const [viewNotif,  setViewNotif]  = useState(null);

  /* ── Filtres Alertes critiques ── */
  const [fUrgence,  setFUrgence]  = useState(() => new Set());
  const [fPaiement, setFPaiement] = useState(() => new Set());

  /* ── Filtres Historique ── */
  const [fHistType,  setFHistType]  = useState(() => new Set());
  const [fHistCanal, setFHistCanal] = useState(() => new Set());
  const [fHistStat,  setFHistStat]  = useState(() => new Set());
  const [histSearch, setHistSearch] = useState('');

  /* ── Rappels / Personnalisées ── */
  const [showPerso, setShowPerso] = useState(false);
  const [persoForm, setPersoForm] = useState(PERSO_INIT);
  const [sendingP,  setSendingP]  = useState(false);

  /* ── Fetch ── */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rC, rCl] = await Promise.all([
        api.get('/coachings/?statut=en_cours'),
        api.get('/clients/'),
      ]);
      setCoachings(Array.isArray(rC.data) ? rC.data : rC.data.results || []);
      setClients(Array.isArray(rCl.data) ? rCl.data : rCl.data.results || []);
    } catch { toast.error('Erreur chargement.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchAll();
    dispatch(fetchNotifications());
  }, [fetchAll, dispatch]);

  useAutoRefresh(fetchNotifications, 30000);

  /* ── Calculs ── */
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const coachingsAlerte = coachings
    .filter(c => c.date_fin)
    .map(c => ({ ...c, jours: diffJ(c.date_fin) }))
    .filter(c => c.jours <= 60)
    .sort((a, b) => a.jours - b.jours);

  const impayes = coachings
    .filter(c => ['impaye', 'partiel'].includes(c.statut_paiement))
    .sort((a, b) => Number(b.prix || 0) - Number(a.prix || 0));

  const anniversaires = clients.filter(c => {
    if (!c.date_naissance_cli) return false;
    const dn = new Date(c.date_naissance_cli);
    const ce = new Date(today.getFullYear(), dn.getMonth(), dn.getDate());
    if (ce < today) ce.setFullYear(today.getFullYear() + 1);
    return Math.ceil((ce - today) / 86400000) <= 30;
  }).map(c => {
    const dn = new Date(c.date_naissance_cli);
    const ce = new Date(today.getFullYear(), dn.getMonth(), dn.getDate());
    if (ce < today) ce.setFullYear(today.getFullYear() + 1);
    return { ...c, jours_anniv: Math.ceil((ce - today) / 86400000) };
  }).sort((a, b) => a.jours_anniv - b.jours_anniv);

  const coachingsFiltres = coachingsAlerte.filter(c =>
    fUrgence.size === 0 || fUrgence.has(urgenceOf(c.jours)));
  const impayesFiltres = impayes.filter(c =>
    fPaiement.size === 0 || fPaiement.has(c.statut_paiement));

  const notifsFiltrees = (sentList || []).filter(n => {
    if (fHistType.size  > 0 && !fHistType.has(n.type_not))  return false;
    if (fHistCanal.size > 0 && !fHistCanal.has(n.canal))    return false;
    if (fHistStat.size  > 0 && !fHistStat.has(n.statut))    return false;
    if (histSearch) {
      const q = histSearch.toLowerCase();
      return (n.destinataire_nom || '').toLowerCase().includes(q) ||
             (n.canal || '').toLowerCase().includes(q);
    }
    return true;
  });

  const persosList = (sentList || []).filter(n => ['message_special', 'fete'].includes(n.type_not));

  /* ── KPI ── */
  const nbJ15    = coachingsAlerte.filter(c => c.jours <= 15).length;
  const nbJ30    = coachingsAlerte.filter(c => c.jours > 15 && c.jours <= 30).length;
  const nbImpaye = impayes.length;
  const nbAnniv  = anniversaires.length;

  /* ── Actions ── */
  const handleNotifier = async ({ clientId, type_not, contenu }) => {
    setSending(clientId);
    try {
      await dispatch(envoyerNotification({
        destinataire_client: clientId, canal: 'SMS', type_not, contenu,
      })).unwrap();
      toast.success('Notification envoyée !');
      dispatch(fetchNotifications());
    } catch { toast.error('Échec envoi.'); }
    finally { setSending(null); }
  };

  const handleSendPerso = async (e) => {
    e.preventDefault();
    if (!persoForm.client) { toast.error('Sélectionnez un client.'); return; }
    if (!persoForm.contenu.trim()) { toast.error('Le message est vide.'); return; }
    setSendingP(true);
    try {
      await dispatch(envoyerNotification({
        destinataire_client: persoForm.client,
        canal:    persoForm.canal,
        type_not: persoForm.type_not,
        contenu:  persoForm.contenu,
        ...(persoForm.date_evenement ? { date_evenement: persoForm.date_evenement } : {}),
      })).unwrap();
      toast.success('Message personnalisé envoyé !');
      setPersoForm(PERSO_INIT);
      setShowPerso(false);
      dispatch(fetchNotifications());
    } catch { toast.error('Échec envoi.'); }
    finally { setSendingP(false); }
  };

  const handlePdf = async (notifId, clientNom) => {
    setPdfLoading(notifId);
    try {
      const r = await api.get(`/notifications/${notifId}/pdf/`, { responseType: 'blob' });
      const url  = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `notification_${notifId}_${(clientNom || '').replace(/\s/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erreur PDF.'); }
    finally { setPdfLoading(null); }
  };

  /* ── Rendu ── */
  return (
    <div className="page-list">

      {/* KPI cards */}
      {!loading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 14 }}>
          {[
            { label: 'Urgences J-15',   value: nbJ15,    icon: AlertTriangle, color: '#C00000' },
            { label: 'Alertes J-30',    value: nbJ30,    icon: CalendarClock, color: '#ED7D31' },
            { label: 'Impayés actifs',  value: nbImpaye, icon: CreditCard,    color: '#6A0DAD' },
            { label: 'Anniversaires',   value: nbAnniv,  icon: Gift,          color: '#1E7145' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="card"
              style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12,
                borderLeft: `3px solid ${color}` }}>
              <div style={{ background: color + '18', borderRadius: 8, padding: 7, flexShrink: 0 }}>
                <Icon size={17} color={color} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#6B7280' }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#1F2937', lineHeight: 1.2 }}>{value}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Onglets */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #E5E7EB', marginBottom: 14 }}>
        {TABS.map(({ key, label, color, icon: Icon }) => {
          const badge =
            key === 'critiques'  ? coachingsAlerte.length + impayes.length :
            key === 'rappels'    ? anniversaires.length + persosList.length :
            key === 'historique' ? (sentList || []).length : 0;
          return (
            <button key={key} onClick={() => setTab(key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 18px', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', background: 'none', border: 'none',
                borderBottom: tab === key ? `3px solid ${color}` : '3px solid transparent',
                marginBottom: -2, color: tab === key ? color : '#6B7280',
                transition: 'all 0.15s',
              }}>
              <Icon size={14} /> {label}
              {badge > 0 && (
                <span style={{ background: color, color: 'white', borderRadius: 99,
                  fontSize: 10, fontWeight: 700, padding: '1px 6px', marginLeft: 2 }}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 4 }}>
          {tab === 'historique' && (
            <div className="search-bar" style={{ width: 200 }}>
              <Search size={14} />
              <input placeholder="Destinataire, canal…" value={histSearch}
                onChange={e => setHistSearch(e.target.value)} style={{ fontSize: 12 }} />
            </div>
          )}
          <button className="header-btn btn-secondary btn-sm"
            onClick={() => { fetchAll(); dispatch(fetchNotifications()); }}
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={13} /> Actualiser
          </button>
        </div>
      </div>

      {loading ? (
        <div className="spinner"><div className="spinner-ring" /> Chargement…</div>
      ) : (
        <>
          {/* ══ ALERTES CRITIQUES ══ */}
          {tab === 'critiques' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, overflow: 'auto' }}>

              {/* Échéances */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 13, fontWeight: 700, color: '#C00000' }}>
                    <CalendarClock size={15} /> Contrats arrivant à échéance (J-60)
                    <span style={{ background: '#C0000018', color: '#C00000', borderRadius: 99,
                      fontSize: 11, fontWeight: 700, padding: '1px 8px' }}>
                      {coachingsAlerte.length}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                  padding: '5px 12px', background: '#F9FAFB', border: '1px solid #E5E7EB',
                  borderRadius: 8, marginBottom: 8 }}>
                  <FilterBar options={URGENCE_OPT} active={fUrgence} setActive={setFUrgence} label="Urgence" />
                  {fUrgence.size > 0 && (
                    <button onClick={() => setFUrgence(new Set())}
                      style={{ fontSize: 11, color: '#C00000', background: 'none', border: 'none',
                        cursor: 'pointer', textDecoration: 'underline', padding: 0, marginLeft: 'auto' }}>
                      Tout afficher
                    </button>
                  )}
                </div>
                <div className="table-container">
                  {coachingsFiltres.length === 0 ? (
                    <div className="empty-state"><CalendarClock size={32} />
                      <p>Aucune échéance dans les 60 jours</p></div>
                  ) : (
                    <table style={{ width: '100%', tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 70 }} /><col style={{ width: '22%' }} />
                        <col style={{ width: '16%' }} /><col style={{ width: '14%' }} />
                        <col style={{ width: 88 }} /><col style={{ width: 90 }} />
                        <col style={{ width: 80 }} /><col style={{ width: 80 }} />
                      </colgroup>
                      <thead><tr>
                        <th style={{ fontSize: 11 }}>Urgence</th>
                        <th style={{ fontSize: 11 }}>Client</th>
                        <th style={{ fontSize: 11 }}>Domaine</th>
                        <th style={{ fontSize: 11 }}>Commercial</th>
                        <th style={{ fontSize: 11 }}>Date fin</th>
                        <th style={{ fontSize: 11 }}>Prix (FCFA)</th>
                        <th style={{ fontSize: 11 }}>Paiement</th>
                        <th style={{ fontSize: 11 }}>Action</th>
                      </tr></thead>
                      <tbody>
                        {coachingsFiltres.map(c => (
                          <tr key={c.id} style={{ background: c.jours <= 15 ? '#FFF5F5' : c.jours <= 30 ? '#FFFBF0' : 'white' }}>
                            <td style={{ padding: '7px 4px', textAlign: 'center' }}><UrgenceBadge jours={c.jours} /></td>
                            <td style={{ fontSize: 12, fontWeight: 700, padding: '7px 6px',
                              overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                              {c.client_nom}
                              <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 400 }}>{c.client_telephone || ''}</div>
                            </td>
                            <td style={{ fontSize: 11, padding: '7px 6px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                              {c.domaine_label || c.domaine}
                            </td>
                            <td style={{ fontSize: 11, padding: '7px 6px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                              {c.commercial_nom || '—'}
                            </td>
                            <td style={{ fontSize: 12, fontWeight: 600, color: '#C00000', padding: '7px 6px' }}>
                              {fmt(c.date_fin)}
                            </td>
                            <td style={{ fontSize: 12, padding: '7px 6px' }}>{fmtN(c.prix)}</td>
                            <td style={{ padding: '7px 4px' }}>
                              <span className={`badge badge-${c.statut_paiement === 'paye' ? 'vert' : c.statut_paiement === 'partiel' ? 'orange' : 'rouge'}`}
                                style={{ fontSize: 10 }}>{c.statut_paiement}</span>
                            </td>
                            <td style={{ padding: '7px 4px' }}>
                              <SendBtn sending={sending === c.client}
                                onClick={() => handleNotifier({
                                  clientId: c.client, type_not: 'contractuelle',
                                  contenu: `Bonjour, votre coaching expire dans ${c.jours} jour(s) (${fmt(c.date_fin)}). Veuillez nous contacter pour un renouvellement.`,
                                })} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Impayés */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 13, fontWeight: 700, color: '#6A0DAD', marginBottom: 8 }}>
                  <CreditCard size={15} /> Impayés en cours
                  <span style={{ background: '#6A0DAD18', color: '#6A0DAD', borderRadius: 99,
                    fontSize: 11, fontWeight: 700, padding: '1px 8px' }}>
                    {impayes.length}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                  padding: '5px 12px', background: '#F9FAFB', border: '1px solid #E5E7EB',
                  borderRadius: 8, marginBottom: 8 }}>
                  <FilterBar options={PAIEMENT_OPT} active={fPaiement} setActive={setFPaiement} label="Statut" />
                  {fPaiement.size > 0 && (
                    <button onClick={() => setFPaiement(new Set())}
                      style={{ fontSize: 11, color: '#C00000', background: 'none', border: 'none',
                        cursor: 'pointer', textDecoration: 'underline', padding: 0, marginLeft: 'auto' }}>
                      Tout afficher
                    </button>
                  )}
                </div>
                <div className="table-container">
                  {impayesFiltres.length === 0 ? (
                    <div className="empty-state"><CreditCard size={32} /><p>Aucun impayé en cours</p></div>
                  ) : (
                    <table style={{ width: '100%', tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: '22%' }} /><col style={{ width: '16%' }} />
                        <col style={{ width: '14%' }} /><col style={{ width: 100 }} />
                        <col style={{ width: 100 }} /><col style={{ width: 100 }} />
                        <col style={{ width: 88 }} /><col style={{ width: 80 }} />
                      </colgroup>
                      <thead><tr>
                        <th style={{ fontSize: 11 }}>Client</th>
                        <th style={{ fontSize: 11 }}>Domaine</th>
                        <th style={{ fontSize: 11 }}>Commercial</th>
                        <th style={{ fontSize: 11 }}>Prix (FCFA)</th>
                        <th style={{ fontSize: 11 }}>Payé (FCFA)</th>
                        <th style={{ fontSize: 11 }}>Statut</th>
                        <th style={{ fontSize: 11 }}>Date fin</th>
                        <th style={{ fontSize: 11 }}>Action</th>
                      </tr></thead>
                      <tbody>
                        {impayesFiltres.map(c => {
                          const reste = Number(c.prix || 0) - Number(c.montant_paye || 0);
                          return (
                            <tr key={c.id}>
                              <td style={{ fontSize: 12, fontWeight: 700, padding: '7px 6px',
                                overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                {c.client_nom}
                                <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 400 }}>{c.client_telephone || ''}</div>
                              </td>
                              <td style={{ fontSize: 11, padding: '7px 6px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                {c.domaine_label || c.domaine}
                              </td>
                              <td style={{ fontSize: 11, padding: '7px 6px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                {c.commercial_nom || '—'}
                              </td>
                              <td style={{ fontSize: 12, padding: '7px 6px' }}>{fmtN(c.prix)}</td>
                              <td style={{ fontSize: 12, padding: '7px 6px', color: '#1E7145', fontWeight: 600 }}>
                                {fmtN(c.montant_paye)}
                              </td>
                              <td style={{ padding: '7px 4px' }}>
                                <span className={`badge badge-${c.statut_paiement === 'partiel' ? 'orange' : 'rouge'}`}
                                  style={{ fontSize: 10 }}>{c.statut_paiement}</span>
                                <div style={{ fontSize: 10, color: '#C00000', marginTop: 2, fontWeight: 600 }}>
                                  Reste : {fmtN(reste)} FCFA
                                </div>
                              </td>
                              <td style={{ fontSize: 11, padding: '7px 6px' }}>{fmt(c.date_fin)}</td>
                              <td style={{ padding: '7px 4px' }}>
                                <SendBtn sending={sending === c.client}
                                  onClick={() => handleNotifier({
                                    clientId: c.client, type_not: 'impaye',
                                    contenu: `Bonjour, un solde de ${fmtN(reste)} FCFA reste dû sur votre contrat. Veuillez régulariser votre situation.`,
                                  })} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ══ RAPPELS ══ */}
          {tab === 'rappels' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, overflow: 'auto' }}>

              {/* Anniversaires */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 13, fontWeight: 700, color: '#1E7145', marginBottom: 8 }}>
                  <Gift size={15} /> Anniversaires dans les 30 prochains jours
                  <span style={{ background: '#1E714518', color: '#1E7145', borderRadius: 99,
                    fontSize: 11, fontWeight: 700, padding: '1px 8px' }}>{anniversaires.length}</span>
                </div>
                <div className="table-container">
                  {anniversaires.length === 0 ? (
                    <div className="empty-state"><Gift size={32} /><p>Aucun anniversaire dans les 30 prochains jours</p></div>
                  ) : (
                    <table style={{ width: '100%', tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: 80 }} /><col style={{ width: '25%' }} />
                        <col style={{ width: '18%' }} /><col style={{ width: '14%' }} />
                        <col style={{ width: 100 }} /><col style={{ width: 60 }} />
                        <col style={{ width: 80 }} />
                      </colgroup>
                      <thead><tr>
                        <th style={{ fontSize: 11 }}>Dans</th>
                        <th style={{ fontSize: 11 }}>Client</th>
                        <th style={{ fontSize: 11 }}>Téléphone</th>
                        <th style={{ fontSize: 11 }}>Commercial</th>
                        <th style={{ fontSize: 11 }}>Date naissance</th>
                        <th style={{ fontSize: 11 }}>Âge</th>
                        <th style={{ fontSize: 11 }}>Action</th>
                      </tr></thead>
                      <tbody>
                        {anniversaires.map(c => {
                          const age = today.getFullYear() - new Date(c.date_naissance_cli).getFullYear();
                          return (
                            <tr key={c.id} style={{ background: c.jours_anniv === 0 ? '#F0FDF4' : 'white' }}>
                              <td style={{ padding: '7px 4px', textAlign: 'center' }}>
                                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99,
                                  color: c.jours_anniv === 0 ? '#1E7145' : '#1F4E79',
                                  background: c.jours_anniv === 0 ? '#D1FAE5' : '#EBF3FB' }}>
                                  {c.jours_anniv === 0 ? "Aujourd'hui 🎂" : `J-${c.jours_anniv}`}
                                </span>
                              </td>
                              <td style={{ fontSize: 12, fontWeight: 700, padding: '7px 6px',
                                overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                {c.prenom_cli} {c.nom_cli}
                              </td>
                              <td style={{ fontSize: 12, padding: '7px 6px' }}>{c.telephone_principal || '—'}</td>
                              <td style={{ fontSize: 11, padding: '7px 6px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                {c.commercial_nom || '—'}
                              </td>
                              <td style={{ fontSize: 12, padding: '7px 6px' }}>{fmt(c.date_naissance_cli)}</td>
                              <td style={{ fontSize: 12, fontWeight: 700, color: '#1F4E79', textAlign: 'center' }}>{age} ans</td>
                              <td style={{ padding: '7px 4px' }}>
                                <SendBtn sending={sending === c.id}
                                  onClick={() => handleNotifier({
                                    clientId: c.id, type_not: 'anniversaire',
                                    contenu: `Joyeux anniversaire ! Toute l'équipe NANCOZI vous souhaite une excellente journée et une année pleine de succès.`,
                                  })} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Personnalisées */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 13, fontWeight: 700, color: '#6A0DAD' }}>
                    <MessageSquarePlus size={15} /> Messages personnalisés
                    <span style={{ background: '#6A0DAD18', color: '#6A0DAD', borderRadius: 99,
                      fontSize: 11, fontWeight: 700, padding: '1px 8px' }}>{persosList.length}</span>
                  </div>
                  {!showPerso && (
                    <button className="header-btn btn-primary btn-sm"
                      onClick={() => setShowPerso(true)}
                      style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <MessageSquarePlus size={13} /> Nouveau message
                    </button>
                  )}
                </div>

                {showPerso && (
                  <div className="card" style={{ marginBottom: 12, border: '2px solid #6A0DAD22' }}>
                    <div style={{ padding: '12px 16px 0', marginBottom: 12,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                        fontSize: 13, fontWeight: 700, color: '#6A0DAD' }}>
                        <MessageSquarePlus size={14} /> Nouveau message personnalisé
                      </div>
                      <button onClick={() => { setShowPerso(false); setPersoForm(PERSO_INIT); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}>
                        <X size={15} />
                      </button>
                    </div>
                    <form onSubmit={handleSendPerso} style={{ padding: '0 16px 16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label required">Client destinataire</label>
                          <select className="form-control" value={persoForm.client}
                            onChange={e => setPersoForm(f => ({ ...f, client: e.target.value }))} required>
                            <option value="">— Sélectionnez —</option>
                            {clients.map(c => (
                              <option key={c.id} value={c.id}>{c.prenom_cli} {c.nom_cli}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Canal d'envoi</label>
                          <select className="form-control" value={persoForm.canal}
                            onChange={e => setPersoForm(f => ({ ...f, canal: e.target.value }))}>
                            <option value="SMS">SMS</option>
                            <option value="Email">Email</option>
                            <option value="WhatsApp">WhatsApp</option>
                          </select>
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Type</label>
                          <select className="form-control" value={persoForm.type_not}
                            onChange={e => setPersoForm(f => ({ ...f, type_not: e.target.value }))}>
                            <option value="message_special">Message spécial</option>
                            <option value="fete">Fête nationale / religieuse</option>
                            <option value="anniversaire">Anniversaire</option>
                            <option value="impaye">Rappel impayé</option>
                            <option value="contractuelle">Alerte contractuelle</option>
                          </select>
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Date événement (optionnel)</label>
                          <input type="date" className="form-control" value={persoForm.date_evenement}
                            onChange={e => setPersoForm(f => ({ ...f, date_evenement: e.target.value }))} />
                        </div>
                      </div>
                      <div className="form-group" style={{ marginBottom: 10 }}>
                        <label className="form-label required">
                          Message <span style={{ color: '#9CA3AF', fontWeight: 400 }}>
                            ({persoForm.contenu.length}/{persoForm.canal === 'SMS' ? 160 : 2000} car.)
                          </span>
                        </label>
                        <textarea className="form-control" rows={3} required
                          value={persoForm.contenu} maxLength={persoForm.canal === 'SMS' ? 160 : 2000}
                          onChange={e => setPersoForm(f => ({ ...f, contenu: e.target.value }))}
                          placeholder="Rédigez votre message personnalisé…" />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <button type="button" className="header-btn btn-secondary"
                          onClick={() => { setShowPerso(false); setPersoForm(PERSO_INIT); }}>Annuler</button>
                        <button type="submit" className="header-btn btn-primary" disabled={sendingP}
                          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Send size={13} /> {sendingP ? 'Envoi…' : 'Envoyer'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                <div className="table-container">
                  {persosList.length === 0 ? (
                    <div className="empty-state"><MessageSquarePlus size={32} /><p>Aucun message personnalisé envoyé</p></div>
                  ) : (
                    <table style={{ width: '100%', tableLayout: 'fixed' }}>
                      <thead><tr>
                        <th style={{ fontSize: 11 }}>Destinataire</th>
                        <th style={{ fontSize: 11 }}>Type</th>
                        <th style={{ fontSize: 11 }}>Canal</th>
                        <th style={{ fontSize: 11 }}>Statut</th>
                        <th style={{ fontSize: 11 }}>Date envoi</th>
                        <th style={{ fontSize: 11, width: 50, textAlign: 'center' }}>Voir</th>
                        <th style={{ fontSize: 11, width: 60, textAlign: 'center' }}>PDF</th>
                      </tr></thead>
                      <tbody>
                        {persosList.map(n => (
                          <tr key={n.id}>
                            <td style={{ fontSize: 12, fontWeight: 600, padding: '7px 6px',
                              overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                              {n.destinataire_nom || '—'}
                            </td>
                            <td style={{ fontSize: 11, padding: '7px 6px' }}>
                              {n.type_not === 'fete' ? 'Fête' : 'Message spécial'}
                            </td>
                            <td style={{ padding: '7px 4px' }}>
                              <span className={`badge badge-${n.canal==='Email'?'bleu':n.canal==='WhatsApp'?'vert':'orange'}`}
                                style={{ fontSize: 10 }}>{n.canal}</span>
                            </td>
                            <td style={{ padding: '7px 4px' }}>
                              <span className={`badge badge-${n.statut==='envoye'?'vert':n.statut==='echec'?'rouge':'orange'}`}
                                style={{ fontSize: 10 }}>{n.statut}</span>
                            </td>
                            <td style={{ fontSize: 11, padding: '7px 6px' }}>
                              {new Date(n.date_heure_envoi).toLocaleString('fr-FR')}
                            </td>
                            <td style={{ padding: '7px 4px', textAlign: 'center' }}>
                              <button onClick={() => setViewNotif(n)} title="Voir"
                                style={{ background: 'none', border: '1.5px solid #6B7280', borderRadius: 6,
                                  cursor: 'pointer', padding: '3px 7px', display: 'inline-flex',
                                  alignItems: 'center', color: '#6B7280' }}>
                                <Eye size={12} />
                              </button>
                            </td>
                            <td style={{ padding: '7px 4px', textAlign: 'center' }}>
                              <button onClick={() => handlePdf(n.id, n.destinataire_nom)}
                                disabled={pdfLoading === n.id}
                                style={{ background: 'none', border: '1.5px solid #6A0DAD', borderRadius: 6,
                                  cursor: pdfLoading === n.id ? 'wait' : 'pointer', padding: '3px 7px',
                                  display: 'inline-flex', alignItems: 'center', gap: 3,
                                  color: '#6A0DAD', fontSize: 10, fontWeight: 600,
                                  opacity: pdfLoading === n.id ? 0.5 : 1 }}>
                                <FileText size={12} />{pdfLoading === n.id ? '…' : 'PDF'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ══ HISTORIQUE ══ */}
          {tab === 'historique' && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
              <div style={{ marginBottom: 10, flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
                  padding: '6px 12px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8 }}>
                  <FilterBar options={HIST_TYPE_OPT}  active={fHistType}  setActive={setFHistType}  label="Type" />
                  <div style={{ width: 1, height: 18, background: '#E5E7EB' }} />
                  <FilterBar options={CANAL_OPT}       active={fHistCanal} setActive={setFHistCanal} label="Canal" />
                  <div style={{ width: 1, height: 18, background: '#E5E7EB' }} />
                  <FilterBar options={STAT_OPT}        active={fHistStat}  setActive={setFHistStat}  label="Statut" />
                  {(fHistType.size > 0 || fHistCanal.size > 0 || fHistStat.size > 0 || histSearch) && (
                    <button onClick={() => { setFHistType(new Set()); setFHistCanal(new Set()); setFHistStat(new Set()); setHistSearch(''); }}
                      style={{ fontSize: 11, color: '#C00000', background: 'none', border: 'none',
                        cursor: 'pointer', textDecoration: 'underline', padding: 0, marginLeft: 'auto' }}>
                      Tout afficher
                    </button>
                  )}
                </div>
              </div>
              <div className="table-container">
                {loadingNotifs ? (
                  <div className="spinner">Chargement…</div>
                ) : notifsFiltrees.length === 0 ? (
                  <div className="empty-state"><Bell size={32} />
                    <p>{fHistType.size || fHistCanal.size || fHistStat.size || histSearch
                      ? 'Aucun résultat' : 'Aucune notification envoyée'}</p>
                  </div>
                ) : (
                  <table style={{ width: '100%', tableLayout: 'fixed' }}>
                    <thead><tr>
                      <th style={{ fontSize: 11 }}>Destinataire</th>
                      <th style={{ fontSize: 11 }}>Type</th>
                      <th style={{ fontSize: 11 }}>Canal</th>
                      <th style={{ fontSize: 11 }}>Statut</th>
                      <th style={{ fontSize: 11 }}>Date envoi</th>
                      <th style={{ fontSize: 11, width: 50, textAlign: 'center' }}>Voir</th>
                      <th style={{ fontSize: 11, width: 60, textAlign: 'center' }}>PDF</th>
                    </tr></thead>
                    <tbody>
                      {notifsFiltrees.map(n => {
                        const typeOpt = HIST_TYPE_OPT.find(t => t.value === n.type_not);
                        return (
                          <tr key={n.id}>
                            <td style={{ fontSize: 12, fontWeight: 600, padding: '7px 6px',
                              overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                              {n.destinataire_nom || '—'}
                            </td>
                            <td style={{ padding: '7px 6px' }}>
                              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 6,
                                background: (typeOpt?.color || '#6B7280') + '18',
                                color: typeOpt?.color || '#6B7280' }}>
                                {typeOpt?.label || n.type_not?.replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td style={{ padding: '7px 4px' }}>
                              <span className={`badge badge-${n.canal==='Email'?'bleu':n.canal==='WhatsApp'?'vert':'orange'}`}
                                style={{ fontSize: 10 }}>{n.canal}</span>
                            </td>
                            <td style={{ padding: '7px 4px' }}>
                              <span className={`badge badge-${n.statut==='envoye'?'vert':n.statut==='echec'?'rouge':'orange'}`}
                                style={{ fontSize: 10 }}>
                                {n.statut === 'envoye' ? 'Envoyé' : n.statut === 'echec' ? 'Échec' : 'En attente'}
                              </span>
                            </td>
                            <td style={{ fontSize: 11, padding: '7px 6px' }}>
                              {new Date(n.date_heure_envoi).toLocaleString('fr-FR')}
                            </td>
                            <td style={{ padding: '7px 4px', textAlign: 'center' }}>
                              <button onClick={() => setViewNotif(n)} title="Voir"
                                style={{ background: 'none', border: '1.5px solid #6B7280', borderRadius: 6,
                                  cursor: 'pointer', padding: '3px 7px', display: 'inline-flex',
                                  alignItems: 'center', color: '#6B7280' }}>
                                <Eye size={12} />
                              </button>
                            </td>
                            <td style={{ padding: '7px 4px', textAlign: 'center' }}>
                              <button onClick={() => handlePdf(n.id, n.destinataire_nom)}
                                disabled={pdfLoading === n.id}
                                style={{ background: 'none', border: '1.5px solid #1F4E79', borderRadius: 6,
                                  cursor: pdfLoading === n.id ? 'wait' : 'pointer', padding: '3px 7px',
                                  display: 'inline-flex', alignItems: 'center', gap: 3,
                                  color: '#1F4E79', fontSize: 10, fontWeight: 600,
                                  opacity: pdfLoading === n.id ? 0.5 : 1 }}>
                                <FileText size={12} />{pdfLoading === n.id ? '…' : 'PDF'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal voir contenu */}
      {viewNotif && (
        <div className="modal-overlay" onClick={() => setViewNotif(null)}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Eye size={16} /> Contenu du message
              </h2>
              <button className="header-btn btn-secondary btn-sm btn-icon" onClick={() => setViewNotif(null)}>
                <X size={14} />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                {[
                  ['Destinataire', viewNotif.destinataire_nom || '—'],
                  ['Canal', viewNotif.canal],
                  ['Date', new Date(viewNotif.date_heure_envoi).toLocaleString('fr-FR')],
                ].map(([k, v]) => (
                  <span key={k} style={{ fontSize: 11, background: '#F3F4F6', borderRadius: 6,
                    padding: '3px 10px', color: '#374151' }}>
                    <b>{k} :</b> {v}
                  </span>
                ))}
              </div>
              <div style={{ background: '#F8FAFF', border: '1.5px solid #E5E7EB', borderRadius: 8,
                padding: '14px 16px', fontSize: 13, color: '#374151', lineHeight: 1.7,
                whiteSpace: 'pre-wrap', minHeight: 80 }}>
                {viewNotif.contenu ||
                  <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Aucun contenu</span>}
              </div>
            </div>
            <div className="modal-footer">
              <button className="header-btn btn-secondary" onClick={() => setViewNotif(null)}>Fermer</button>
              <button className="header-btn btn-primary"
                onClick={() => { handlePdf(viewNotif.id, viewNotif.destinataire_nom); setViewNotif(null); }}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <FileText size={14} /> Télécharger PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
